import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThemeService } from '../core/services/theme.service';

@Injectable({
    providedIn: 'root'
})
export class LogoService {
    private readonly DEFAULT_LOGO = 'assets/images/logo.png';
    private currentLogo: string = this.DEFAULT_LOGO;
    private logoSubject = new BehaviorSubject<string>(this.DEFAULT_LOGO);

    constructor(private themeService: ThemeService) {
        // Set default logo on initialization
        this.setDefaultLogo();

        // Suscribirse a cambios del tema para actualizar el logo
        this.themeService.currentTheme$.subscribe(() => {
            // Pequeño delay para asegurar que las variables CSS ya están aplicadas
            setTimeout(() => {
                this.setDefaultLogo();
            }, 100);
        });
    }

    private setDefaultLogo(): void {
        // Primero verificar si hay un logo personalizado del tema
        const themeLogoUrl = getComputedStyle(document.documentElement)
            .getPropertyValue('--topbar-logo-url')?.trim();

        if (themeLogoUrl && themeLogoUrl !== '' && themeLogoUrl !== 'none') {
            // Usar logo del tema, eliminar url() wrapper si existe
            const cleanUrl = themeLogoUrl.replace(/url\(['"]?([^'"]*)['"]?\)/, '$1');
            document.documentElement.style.setProperty('--logo-url', `url('${cleanUrl}')`);
            this.currentLogo = cleanUrl;
            this.logoSubject.next(cleanUrl);
        } else {
            // Usar logo por defecto
            document.documentElement.style.setProperty('--logo-url', `url('${this.DEFAULT_LOGO}')`);
            this.currentLogo = this.DEFAULT_LOGO;
            this.logoSubject.next(this.DEFAULT_LOGO);
        }
    }

    setLogoUrl(logoUrl: string | null): void {
        if (!logoUrl) {
            this.setDefaultLogo();
            return;
        }

        try {
            if (logoUrl.startsWith('data:image')) {
                // For base64 images, set directly
                document.documentElement.style.setProperty('--logo-url', `url('${logoUrl}')`);
                this.currentLogo = logoUrl;
                this.logoSubject.next(logoUrl);
            } else {
                // For regular URLs, validate first
                const img = new Image();
                img.onload = () => {
                    document.documentElement.style.setProperty('--logo-url', `url('${logoUrl}')`);
                    this.currentLogo = logoUrl;
                    this.logoSubject.next(logoUrl);
                };
                img.onerror = () => {
                    console.warn('Failed to load logo, falling back to default');
                    this.setDefaultLogo();
                    this.currentLogo = this.DEFAULT_LOGO;
                    this.logoSubject.next(this.DEFAULT_LOGO);
                };
                img.src = logoUrl;
            }
        } catch (error) {
            console.error('Error setting logo:', error);
            this.setDefaultLogo();
            this.currentLogo = this.DEFAULT_LOGO;
            this.logoSubject.next(this.DEFAULT_LOGO);
        }
    }

    getLogoUrl(): string {
        return this.currentLogo;
    }
} 